import React from 'react';

import { isRevisionPageDocument, type RevisionPage } from '@gitbook/hive-core';
import { createPalette, type PaletteCommandDef, type PaletteDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { IconDefinition } from '@gitbook/spine-react';

import { PROMPT_LANGUAGES } from '../../utils';

type PromptTemplate<Context = {}> = {
    /**
     * The id of the prompt. Used for analytics.
     */
    id: string;

    /**
     * The icon of the prompt.
     */
    icon?: IconDefinition;

    /**
     * The title of the prompt.
     */
    title: string;

    /**
     * The prompt to be used when the prompt is triggered.
     * If it finishes with a `#`, the user will be prompted to fill a page, or a page will be inserted automatically.
     */
    prompt: string;

    /**
     * Check if the prompt should be shown for the given context.
     */
    check?: (context: Context) => boolean;

    /**
     * Whether the prompt should be posted immediately in the agent or set as a draft value.
     */
    immediate?: boolean;

    /**
     * Optional prompts to show in a nested menu under this prompt.
     */
    descendant?: {
        commands: PromptTemplate<Context>[];
        search?: PaletteDef['search'];
    };
};

type PromptemplateGroup<Context = {}> = {
    /**
     * The id of the group.
     */
    id: string;

    /**
     * The title of the group.
     */
    title?: string;

    /**
     * The icon of the group.
     */
    icon?: IconDefinition;

    /**
     * The prompts in the group.
     */
    prompts: PromptTemplate<Context>[];
};

export const GLOBAL_PROMPT_TEMPLATES: Array<PromptemplateGroup> = [
    {
        id: 'global',
        prompts: [
            {
                id: 'global.improve-page',
                icon: Icon.AIMakeShorter,
                title: 'Create fun intro',
                prompt: 'Review the first pages and create a new intro page introducing the documentation in a fun way',
            },
        ],
    },
];

export const PAGE_PROMPT_TEMPLATES: Array<PromptemplateGroup<{ page: RevisionPage }>> = [
    {
        id: 'structure',
        prompts: [
            {
                id: 'page.icon',
                icon: Icon.EmojiAddReaction,
                title: 'Generate an icon',
                prompt: 'Generate an icon for the page #',
                check: (context) => !context.page.icon && !context.page.emoji,
            },
            {
                id: 'page.description',
                icon: Icon.ColumnTypeText,
                title: 'Generate a description',
                prompt: 'Generate a description for the page #',
                check: (context) =>
                    isRevisionPageDocument(context.page) && !context.page.description,
            },
        ],
    },
    {
        id: 'content',
        icon: Icon.AISimplifyLanguage,
        prompts: [
            {
                id: 'content.spelling',
                title: 'Fix spelling and grammar',
                prompt: 'Fix spelling and grammar in the page #',
            },
            {
                id: 'content.consistency',
                title: 'Rewrite for consistency',
                prompt: 'Rewrite the content for consistency #',
            },
            {
                id: 'content.seo',
                title: 'Optimize for SEO',
                prompt: 'Optimize the content for better search engine visibility by using relevant keywords on page #',
            },
            {
                id: 'content.add-summary',
                title: 'Add summary and next steps',
                prompt: 'Add a concise summary and next steps at the end for the page #',
            },
            {
                id: 'content.link-related',
                title: 'Link related topics',
                prompt: 'Identify and link related topics to improve navigation on page #',
            },
        ],
    },
    {
        id: 'pages',
        icon: Icon.AddPageGroup,
        prompts: [
            {
                id: 'pages.divide',
                title: 'Divide into multiple pages',
                prompt: 'Divide into multiple pages, the page #',
            },
            {
                id: 'pages.merge',
                title: 'Combine into a single page',
                prompt: 'Merge descendant pages into the page #',
                check: (context) =>
                    isRevisionPageDocument(context.page) && context.page.pages.length > 0,
            },
        ],
    },
];

export const MENU_PROMPT_LIST: Array<PromptemplateGroup> = [
    {
        id: 'improve',
        title: 'Improve',
        prompts: [
            {
                id: 'improve.consistency-styleguide',
                icon: Icon.Check,
                title: 'Check consistency against style guide',
                prompt: 'Check consistency against style guide',
                immediate: true,
            },
            {
                id: 'improve.review-this',
                icon: Icon.Edit,
                title: 'Review and improve this page',
                prompt: 'Review and improve this page',
                immediate: true,
            },
            {
                id: 'improve.typos',
                icon: Icon.AIFixSpellingGrammar,
                title: 'Fix all typos and grammar',
                prompt: 'Fix all typos and grammar in this page',
                immediate: true,
            },
            {
                id: 'improve.generate-page-icon',
                icon: Icon.EmojiReaction,
                title: 'Generate page icon',
                prompt: 'Generate an icon for this page',
                immediate: true,
            },
            {
                id: 'improve.page-layout',
                icon: Icon.Type,
                title: 'Improve page layout',
                prompt: 'Improve the layout of this page',
                immediate: true,
            },
            {
                id: 'improve.page-description',
                icon: Icon.AI,
                title: 'Generate page description',
                prompt: 'Generate a description for this page',
                immediate: true,
            },
            {
                id: 'improve.fix-heading-structure',
                icon: Icon.Heading1,
                title: 'Fix heading structure',
                prompt: 'Fix the heading structure of this page to ensure it is properly nested and follows a logical hierarchy',
                immediate: true,
            },
            {
                id: 'improve.split-into-multiple-pages',
                icon: Icon.AddPageGroup,
                title: 'Divide into multiple pages',
                prompt: 'Divide this page into multiple pages',
                immediate: true,
            },
            {
                id: 'improve.add-summary-next-steps',
                icon: Icon.Stepper,
                title: 'Add summary and next steps',
                prompt: 'Add a concise summary and next steps at the end of this page',
                immediate: true,
            },
            {
                id: 'improve.link-related-topics',
                icon: Icon.Link,
                title: 'Link related topics',
                prompt: 'Identify and link related topics to improve navigation on this page',
                immediate: true,
            },
        ],
    },
    {
        id: 'rewrite',
        title: 'Rewrite',
        prompts: [
            {
                id: 'rewrite.translate',
                icon: Icon.AITranslate,
                title: 'Translate page to',
                prompt: 'Translate this page into another language',
                immediate: true,
                descendant: {
                    search: {
                        placeholder: 'Find a language',
                    },
                    commands: PROMPT_LANGUAGES.map((language) => ({
                        id: `rewrite.translate.${language.code.toLowerCase()}`,
                        prefix: React.createElement(language.code),
                        title: language.name,
                        prompt: `Translate this page into ${language.name}`,
                        immediate: true,
                    })),
                },
            },
            {
                id: 'rewrite.simplify-language',
                icon: Icon.AISimplifyLanguage,
                title: 'Simplify language',
                prompt: 'Simplify the language of this page to make it more accessible',
                immediate: true,
            },
            {
                id: 'rewrite.shorter',
                icon: Icon.AIMakeShorter,
                title: 'Make it shorter',
                prompt: 'Make the content of this page more concise while retaining key information',
                immediate: true,
            },
            {
                id: 'rewrite.longer',
                icon: Icon.AIMakeLonger,
                title: 'Make it longer',
                prompt: 'Make the content of this page more detailed by adding relevant information and examples',
                immediate: true,
            },
            {
                id: 'rewrite.optimize-seo',
                icon: Icon.Globe,
                title: 'Optimize SEO',
                prompt: 'Optimize the SEO of this page to improve search engine ranking',
                immediate: true,
            },
        ],
    },
    {
        id: 'summarize',
        title: 'Summarize',
        prompts: [
            {
                id: 'summarize.summarize',
                icon: Icon.Quote,
                title: 'Summarize this page',
                prompt: 'Summarize this page',
                immediate: true,
            },
            {
                id: 'summarize.explain',
                icon: Icon.Question,
                title: 'Explain this page',
                prompt: 'Explain this page in simple terms',
                immediate: true,
            },
        ],
    },
];

const selectionPromptIds = [
    'improve.review-this',
    'improve.typos',
    'rewrite.translate',
    'rewrite.simplify-language',
    'rewrite.shorter',
    'rewrite.longer',
    'summarize.summarize',
    'summarize.explain',
] as const;

type SelectionMenuPromptId = (typeof selectionPromptIds)[number];
const SELECTION_MENU_PROMPT_IDS = new Set<SelectionMenuPromptId>(selectionPromptIds);

const SELECTION_MENU_PROMPT_OVERRIDES: Record<
    SelectionMenuPromptId,
    { title: string; prompt: string; descendant?: PromptTemplate['descendant'] }
> = {
    'improve.review-this': {
        title: 'Review and improve selection',
        prompt: 'Review and improve this selection',
    },
    'improve.typos': {
        title: 'Fix typos and grammar',
        prompt: 'Fix typos and grammar for this selection',
    },
    'rewrite.translate': {
        title: 'Translate selection to',
        prompt: 'Translate this selection into another language',
        descendant: {
            search: {
                placeholder: 'Find a language',
            },
            commands: PROMPT_LANGUAGES.map((language) => ({
                id: `rewrite.translate.${language.code.toLowerCase()}`,
                prefix: React.createElement(language.code),
                title: language.name,
                prompt: `Translate this selection into ${language.name}`,
                immediate: true,
            })),
        },
    },
    'rewrite.simplify-language': {
        title: 'Simplify language',
        prompt: 'Simplify the language of this selection to make it more accessible',
    },
    'rewrite.shorter': {
        title: 'Make it shorter',
        prompt: 'Make this selection more concise while retaining key information',
    },
    'rewrite.longer': {
        title: 'Make it longer',
        prompt: 'Make this selection more detailed by adding relevant information and examples',
    },
    'summarize.summarize': {
        title: 'Summarize selection',
        prompt: 'Summarize this selection',
    },
    'summarize.explain': {
        title: 'Explain selection',
        prompt: 'Explain this selection in simple terms',
    },
};

/**
 * Menu prompts that can act on the current editor selection instead of a full page.
 */
export const SELECTION_MENU_PROMPT_LIST = MENU_PROMPT_LIST.map((group) => ({
    ...group,
    prompts: group.prompts
        .filter((prompt) => SELECTION_MENU_PROMPT_IDS.has(prompt.id))
        .map((prompt) => ({
            ...prompt,
            ...SELECTION_MENU_PROMPT_OVERRIDES[prompt.id as SelectionMenuPromptId],
        })),
})).filter((group) => group.prompts.length > 0);

/**
 * Menu prompts shown when asking the agent from an empty editor block in a page with existing content.
 */
export const INSERT_BLOCK_AGENT_MENU_PROMPT_LIST: Array<PromptemplateGroup> = [
    {
        id: 'writing',
        title: 'Writing',
        prompts: [
            {
                id: 'writing.continue',
                icon: Icon.AIContinueWriting,
                title: 'Continue writing',
                prompt: 'Continue this page from the empty block. Use the existing page content for context and write the next paragraph only.',
                immediate: true,
            },
            {
                id: 'writing.add-detail',
                icon: Icon.Type,
                title: 'Add more detail',
                prompt: 'Add a useful paragraph at this empty block that expands on the surrounding page content.',
                immediate: true,
            },
            {
                id: 'writing.add-example',
                icon: Icon.Code,
                title: 'Add an example',
                prompt: 'Add a practical example at this empty block based on the surrounding page content.',
                immediate: true,
            },
            {
                id: 'writing.add-summary',
                icon: Icon.ColumnTypeText,
                title: 'Add a summary',
                prompt: 'Add a short summary paragraph at this point in the page.',
                immediate: true,
            },
            {
                id: 'writing.step-by-step-guide',
                icon: Icon.Stepper,
                title: 'Add step-by-step instructions',
                prompt: 'Insert step-by-step instructions at this point in the page, using the surrounding content as context.',
                immediate: true,
            },
        ],
    },
    {
        id: 'structure',
        prompts: [
            {
                id: 'structure.suggest-blocks',
                icon: Icon.AIStartPrompt,
                title: 'Suggest blocks',
                prompt: 'Suggest a few useful blocks that could be inserted at this point in the page. Keep each suggestion concise.',
                immediate: true,
            },
            {
                id: 'structure.summarize-page',
                icon: Icon.Quote,
                title: 'Summarize page',
                prompt: 'Summarize this page',
                immediate: true,
            },
            {
                id: 'structure.explain-page',
                icon: Icon.Question,
                title: 'Explain page',
                prompt: 'Explain this page in simple terms',
                immediate: true,
            },
        ],
    },
];

const EMPTY_DOCUMENT_INSERT_BLOCK_AGENT_MENU_PROMPT_LIST: Array<PromptemplateGroup> = [
    {
        id: 'writing',
        title: 'Writing',
        prompts: [
            {
                id: 'writing.start',
                icon: Icon.AIContinueWriting,
                title: 'Start writing',
                prompt: 'Start writing this page. Use the page title and any available context to write a clear opening paragraph.',
                immediate: true,
            },
            {
                id: 'writing.introduction',
                icon: Icon.Type,
                title: 'Draft an introduction',
                prompt: 'Draft an introduction for this page.',
                immediate: true,
            },
            {
                id: 'writing.step-by-step-guide',
                icon: Icon.Stepper,
                title: 'Add step-by-step instructions',
                prompt: 'Insert step-by-step instructions for this page.',
                immediate: true,
            },
        ],
    },
    {
        id: 'structure',
        prompts: [
            {
                id: 'structure.suggest-blocks',
                icon: Icon.AIStartPrompt,
                title: 'Suggest blocks',
                prompt: 'Suggest a few useful blocks that could help start this page. Keep each suggestion concise.',
                immediate: true,
            },
            {
                id: 'structure.title-description',
                icon: Icon.ColumnTypeText,
                title: 'Suggest a page title and description',
                prompt: 'Suggest a page title and description for this page. If the current page title is untitled or generic, suggest a better title.',
                immediate: true,
            },
        ],
    },
];

/**
 * Return the insert-block agent menu prompts for the current document state.
 */
export function getInsertBlockAgentMenuPromptList(options: {
    isDocumentEmpty: boolean;
}): Array<PromptemplateGroup> {
    return options.isDocumentEmpty
        ? EMPTY_DOCUMENT_INSERT_BLOCK_AGENT_MENU_PROMPT_LIST
        : INSERT_BLOCK_AGENT_MENU_PROMPT_LIST;
}

/**
 * Resolved prompt template ready to be handled by the caller.
 */
export type CompiledPromptTemplate = {
    id: string;
    prompt: string;
    immediate: boolean;
};

/**
 * Compile a set of prompt templates into a menu.
 */
export function compilePromptTemplates<Context>(
    groups: Array<PromptemplateGroup<Context>>,
    context: Context | null,
    implementPrompt: (input: CompiledPromptTemplate) => void
): PaletteDef {
    const toCommand = (
        prompt: PromptTemplate<Context>,
        icon?: IconDefinition
    ): PaletteCommandDef => {
        const command = {
            key: prompt.id,
            title: prompt.title,
            icon,
        };

        const { descendant } = prompt;
        if (descendant) {
            return {
                ...command,
                descendant: () =>
                    createPalette({
                        search: descendant.search,
                        groups: [
                            {
                                key: prompt.id,
                                commands: descendant.commands.map((item) => toCommand(item)),
                            },
                        ],
                    }),
            };
        }

        return {
            ...command,
            trigger: () => {
                implementPrompt({
                    id: prompt.id,
                    prompt: prompt.prompt,
                    immediate: Boolean(prompt.immediate),
                });
            },
        };
    };

    return createPalette({
        groups: groups.map((group) => ({
            key: group.id,
            title: group.title,
            commands: group.prompts
                .filter((prompt) => (context === null ? true : (prompt.check?.(context) ?? true)))
                .map((prompt, index) =>
                    toCommand(prompt, prompt.icon ?? (index === 0 ? group.icon : undefined))
                ),
        })),
    });
}
